import { IMG_CACHE } from '../../Constants/cache'

export type ImageProps = {
    src: string
    alt: string
    className?: string
  }

const openImgCache = async (): Promise<Cache> => {
  return caches.open(IMG_CACHE.name)
}

/**
 * Retrieves an image Response object from the cache.
 * Returns null if not found or expired.
 */
export const getFromImgCache = async (url: string): Promise<Response | null> => {
  try {
    const cache = await openImgCache()
    const cachedResponse = await cache.match(url)

    if (!cachedResponse) {
      return null
    }

    const timestampHeader = cachedResponse.headers.get(IMG_CACHE.timestampKey)
    if (timestampHeader) {
      const timestamp = parseInt(timestampHeader, 10)
      const expirationTime = timestamp + IMG_CACHE.expirationDays * 24 * 60 * 60 * 1000
      if (Date.now() > expirationTime) {
        await cache.delete(url)
        return null
      }
    }

    return cachedResponse
  } catch (error) {
    console.error('Error retrieving from cache:', error)
    return null
  }
}

/**
 * Adds an image Response to the cache with a timestamp.
 */
export const addToImgCache = async (url: string, response: Response): Promise<void> => {
  try {
    const cache = await openImgCache()
    const responseToCache = response.clone()

    const headers = new Headers(responseToCache.headers)
    headers.set(IMG_CACHE.timestampKey, Date.now().toString())

    const responseWithTimestamp = new Response(responseToCache.body, {
      status: responseToCache.status,
      statusText: responseToCache.statusText,
      headers: headers,
    })

    await cache.put(url, responseWithTimestamp)
    await cleanImgCache()
  } catch (error) {
    console.error('Error adding to cache:', error)
  }
}

/**
 * Cleans the cache by removing expired items and enforcing size limits.
 */
const cleanImgCache = async (): Promise<void> => {
  try {
    const cache = await openImgCache()
    const keys = await cache.keys()
    const cacheEntries: { url: string; timestamp: number }[] = []

    for (const request of keys) {
      const response = await cache.match(request)
      if (response) {
        const timestampHeader = response.headers.get(IMG_CACHE.timestampKey)
        if (timestampHeader) {
          const timestamp = parseInt(timestampHeader, 10)
          const expirationTime = timestamp + IMG_CACHE.expirationDays * 24 * 60 * 60 * 1000

          if (Date.now() > expirationTime) {
            // Delete expired entry
            await cache.delete(request.url)
          } else {
            cacheEntries.push({ url: request.url, timestamp })
          }
        }
      }
    }

    if (cacheEntries.length > IMG_CACHE.maxSize) {
      cacheEntries.sort((a, b) => a.timestamp - b.timestamp)

    const entriesToDelete = cacheEntries.length - IMG_CACHE.maxSize

      for (let i = 0; i < entriesToDelete; i++) {
        await cache.delete(cacheEntries[i].url)
      }
    }
  } catch (error) {
    console.error('Error cleaning cache:', error)
  }
}